import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBlogs, resolveAssetUrl } from '../api/api';
import Footer from '../components/Footer';

const CATEGORY_KEYS = ['all', 'technical', 'news', 'event', 'announcement', 'general'];

const categoryColors = {
  technical: '#0f3460',
  news: '#e8920c',
  event: '#198754',
  announcement: '#dc3545',
  general: '#6c757d',
  all: '#6c757d',
};

export default function Blog() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [blogs, setBlogs] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    const cat = activeCategory === 'all' ? undefined : activeCategory;
    getBlogs(cat)
      .then((res) => setBlogs(res.data))
      .catch(() => setError('Failed to load blogs'))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  const categoryLabel = (key) => {
    const map = {
      all: t('Home.Blog.All'),
      technical: t('Home.Blog.Technical'),
      news: t('Home.Blog.News'),
      event: t('Home.Blog.Event'),
      announcement: t('Home.Blog.Announcement'),
      general: t('Home.Blog.General'),
    };
    return map[key] || key;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Hero */}
      <div
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)',
          padding: '100px 0 60px',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <h1 style={{ fontWeight: 700, fontSize: '2.5rem' }}>{t('Home.Blog.HeroTitle')}</h1>
        <p style={{ opacity: 0.8, fontSize: '1.1rem', marginTop: '8px' }}>
          {t('Home.Blog.HeroSubtitle')}
        </p>
      </div>

      <div className="container py-5">
        {/* Category Filter */}
        <div className="d-flex flex-wrap gap-2 justify-content-center mb-5">
          {CATEGORY_KEYS.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: '8px 20px',
                borderRadius: '999px',
                border: `2px solid ${categoryColors[cat]}`,
                background: activeCategory === cat ? categoryColors[cat] : 'white',
                color: activeCategory === cat ? 'white' : categoryColors[cat],
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {categoryLabel(cat)}
            </button>
          ))}
        </div>

        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status" />
          </div>
        )}

        {error && <div className="alert alert-danger text-center">{error}</div>}

        {!loading && !error && blogs.length === 0 && (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-journal-x" style={{ fontSize: '3rem' }} />
            <p className="mt-3">{t('Home.Blog.NoPosts')}</p>
          </div>
        )}

        <div className="row g-4">
          {blogs.map((blog) => (
            <div key={blog.id} className="col-md-6 col-lg-4">
              <Link
                to={`/blog/${blog.slug}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    height: '100%',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)';
                  }}
                >
                  {blog.coverImage ? (
                    <img
                      src={resolveAssetUrl(blog.coverImage)}
                      alt={blog.title}
                      style={{ width: '100%', height: '180px', objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      style={{
                        height: '180px',
                        background: `linear-gradient(135deg, ${categoryColors[blog.category]}22, ${categoryColors[blog.category]}44)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <i
                        className="bi bi-journal-text"
                        style={{ fontSize: '3rem', color: categoryColors[blog.category] }}
                      />
                    </div>
                  )}
                  <div style={{ padding: '20px' }}>
                    <span
                      style={{
                        background: categoryColors[blog.category],
                        color: 'white',
                        padding: '3px 10px',
                        borderRadius: '999px',
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {categoryLabel(blog.category)}
                    </span>
                    <h5 style={{ marginTop: '12px', fontWeight: 700, lineHeight: 1.4 }}>
                      {blog.title}
                    </h5>
                    {blog.excerpt && (
                      <p style={{ color: '#6c757d', fontSize: '14px', marginTop: '8px' }}>
                        {blog.excerpt.length > 100 ? blog.excerpt.slice(0, 100) + '…' : blog.excerpt}
                      </p>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '16px',
                        fontSize: '13px',
                        color: '#6c757d',
                        flexDirection: isRTL ? 'row-reverse' : 'row',
                      }}
                    >
                      <i className="bi bi-person-circle" />
                      <span>{blog.author?.fullName || 'ENGPI'}</span>
                      <span style={{ marginInlineStart: 'auto' }}>
                        {new Date(blog.createdAt).toLocaleDateString(i18n.language)}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}
